import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { getFirestore, doc, getDoc, updateDoc, collection, getDocs, orderBy, query } from 'firebase/firestore';
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { auth } from '../firebase';
import { updateStimulusState } from '../catalog/catalogApi';
import {
  REQUIRED_LABELS,
  hasValidBox,
  isActiveLabelingWork,
  isLabelingIncomplete,
  orderBatchFirst,
} from './labelStatus';

export default function ViewStimulusDetail() {
  const { hash } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queueMode = searchParams.get('mode') === 'queue';

  // Hashes from a just-uploaded batch, threaded through every queue-mode
  // navigation so batch items are labeled in sequence before the rest.
  const batchHashes = useMemo(() => (
    Array.isArray(location.state?.batchHashes) ? location.state.batchHashes : []
  ), [location.state]);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [labels, setLabels] = useState({});
  const [stimulus, setStimulus] = useState(null);
  const [stateUpdating, setStateUpdating] = useState(false);
  const [stateError, setStateError] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentBox, setCurrentBox] = useState(null);
  const [pendingLabel, setPendingLabel] = useState(null);
  const [labelName, setLabelName] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [queueHashes, setQueueHashes] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  const LABEL_OPTIONS = REQUIRED_LABELS;

  // Draw all existing labels on the canvas
  const drawExistingLabels = () => {
    if (!canvasRef.current || !imageLoaded || !Object.keys(labels).length) return;

    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    
    Object.entries(labels).forEach(([name, coords]) => {
      drawBox(ctx, coords.x1, coords.x2, coords.y1, coords.y2, name);
    });
  };

  // Effect to draw labels when they're loaded or image loads
  useEffect(() => {
    drawExistingLabels();
  }, [labels, imageLoaded]);

  useEffect(() => {
    const fetchQueue = async () => {
      if (!queueMode) {
        setQueueHashes([]);
        return;
      }

      try {
        setQueueLoading(true);
        const db = getFirestore();
        const snapshot = await getDocs(query(collection(db, 'images'), orderBy('hash')));
        const queueItems = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((stimulus) => isActiveLabelingWork(stimulus))
          .map((stimulus) => stimulus.hash || stimulus.id);
        setQueueHashes(orderBatchFirst(queueItems, batchHashes));
      } catch (err) {
        console.error('Error fetching stimulus queue:', err);
      } finally {
        setQueueLoading(false);
      }
    };

    fetchQueue();
  }, [queueMode, hash, batchHashes]);

  useEffect(() => {
    if (queueMode && hash) {
      try {
        localStorage.setItem('vasi_admin_last_queue_hash', hash);
      } catch {
        // no-op for environments where localStorage is blocked
      }
    }
  }, [queueMode, hash]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch image URL
        const storage = getStorage();
        const storageRef = ref(storage, `vasi/stimuli/${hash}.jpg`);
        const url = await getDownloadURL(storageRef);
        setImageUrl(url);

        // Fetch existing labels
        const db = getFirestore();
        const docRef = doc(db, 'images', hash);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setStimulus({ id: docSnap.id || hash, ...data, hash: data.hash || hash });
          setLabels(data.labels || {});
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    if (hash) {
      fetchData();
    }
  }, [hash]);

  const handleMouseDown = (e) => {
    if (pendingLabel) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setIsDrawing(true);
    setStartPoint({ x, y });
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || pendingLabel) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.min(Math.max(0, e.clientX - rect.left), 240);
    const y = Math.min(Math.max(0, e.clientY - rect.top), 360);

    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    
    // Draw existing labels
    Object.entries(labels).forEach(([name, coords]) => {
      drawBox(ctx, coords.x1, coords.x2, coords.y1, coords.y2, name);
    });

    // Draw current box
    const width = x - startPoint.x;
    const height = y - startPoint.y;
    ctx.strokeStyle = '#4F46E5';
    ctx.lineWidth = 2;
    ctx.strokeRect(startPoint.x, startPoint.y, width, height);
    
    setCurrentBox({
      x1: Math.min(startPoint.x, x),
      x2: Math.max(startPoint.x, x),
      y1: Math.min(startPoint.y, y),
      y2: Math.max(startPoint.y, y)
    });
  };

  const handleMouseUp = () => {
    if (!isDrawing || pendingLabel) return;
    
    setIsDrawing(false);
    if (currentBox) {
      setPendingLabel(currentBox);
    }
  };

  const drawBox = (ctx, x1, x2, y1, y2, name) => {
    ctx.strokeStyle = '#4F46E5';
    ctx.lineWidth = 2;
    ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
    
    if (name) {
      ctx.fillStyle = '#4F46E5';
      ctx.font = '12px sans-serif';
      ctx.fillText(name, x1, y1 - 5);
    }
  };

  const handleSaveLabel = async () => {
    if (!labelName || !pendingLabel) return;

    try {
      const currentQueueIndex = queueHashes.indexOf(hash);
      const newLabels = {
        ...labels,
        [labelName]: pendingLabel
      };

      const db = getFirestore();
      const docRef = doc(db, 'images', hash);
      await updateDoc(docRef, { labels: newLabels });

      setLabels(newLabels);
      setStimulus((current) => ({ ...current, labels: newLabels }));
      setLabelName('');
      setPendingLabel(null);

      // Redraw canvas
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      Object.entries(newLabels).forEach(([name, coords]) => {
        drawBox(ctx, coords.x1, coords.x2, coords.y1, coords.y2, name);
      });

      if (queueMode) {
        const isComplete = REQUIRED_LABELS.every((name) => hasValidBox(newLabels[name]));
        if (isComplete) {
          setQueueHashes((prev) => prev.filter((queueHash) => queueHash !== hash));
          const nextHash = queueHashes[currentQueueIndex + 1] || queueHashes[currentQueueIndex - 1];
          if (nextHash) {
            navigate(`/stimuli/${nextHash}?mode=queue`, { state: { batchHashes } });
          } else {
            navigate('/stimuli/queue', { state: { batchHashes } });
          }
        }
      }
    } catch (err) {
      console.error('Error saving label:', err);
      setError('Failed to save label');
    }
  };

  const handleCancelLabel = () => {
    setPendingLabel(null);
    setLabelName('');
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    Object.entries(labels).forEach(([name, coords]) => {
      drawBox(ctx, coords.x1, coords.x2, coords.y1, coords.y2, name);
    });
  };

  const handleDeleteLabel = async (labelName) => {
    try {
      const newLabels = { ...labels };
      delete newLabels[labelName];

      const db = getFirestore();
      const docRef = doc(db, 'images', hash);
      await updateDoc(docRef, { labels: newLabels });

      setLabels(newLabels);
      setStimulus((current) => ({ ...current, labels: newLabels }));

      // Redraw canvas
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      Object.entries(newLabels).forEach(([name, coords]) => {
        drawBox(ctx, coords.x1, coords.x2, coords.y1, coords.y2, name);
      });
    } catch (err) {
      console.error('Error deleting label:', err);
      setError('Failed to delete label');
    }
  };

  const handleBack = () => {
    if (queueMode) {
      navigate('/stimuli/queue', { state: { batchHashes } });
      return;
    }
    const page = searchParams.get('page') || '1';
    navigate(`/stimuli?page=${page}`);
  };

  const queueIndex = queueHashes.indexOf(hash);

  const updateLabelingCompletion = async (labelingComplete) => {
    setStateUpdating(true);
    setStateError('');
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await updateStimulusState({
        token,
        hash,
        labelingComplete,
      });
      setStimulus((current) => ({
        ...current,
        labeling_complete: response.labeling_complete,
      }));

      if (queueMode && labelingComplete) {
        setQueueHashes((current) => current.filter((queueHash) => queueHash !== hash));
        const nextHash = queueHashes[queueIndex + 1] || queueHashes[queueIndex - 1];
        navigate(nextHash ? `/stimuli/${nextHash}?mode=queue` : '/stimuli/queue', {
          state: { batchHashes },
        });
      }
    } catch (err) {
      console.error('Failed to update labeling completion:', err);
      setStateError(err.message || 'Labeling completion could not be updated.');
    } finally {
      setStateUpdating(false);
    }
  };

  const navigateToQueueIndex = (index) => {
    if (index < 0 || index >= queueHashes.length) return;
    navigate(`/stimuli/${queueHashes[index]}?mode=queue`, { state: { batchHashes } });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-x-1.5 text-sm font-semibold text-gray-900"
        >
          <ArrowLeftIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
          {queueMode ? 'Back to Label Queue' : 'Back to Stimuli'}
        </button>
      </div>

      {queueMode && (
        <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-emerald-900">Queue Labeling Mode</p>
              <p className="text-sm text-emerald-800">
                {queueLoading
                  ? 'Refreshing queue...'
                  : queueHashes.length === 0
                    ? 'Queue is empty. All required labels are complete.'
                    : queueIndex >= 0
                      ? `Item ${queueIndex + 1} of ${queueHashes.length}`
                      : `This stimulus is no longer in queue. Remaining: ${queueHashes.length}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigateToQueueIndex(queueIndex - 1)}
                disabled={queueLoading || queueIndex <= 0}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => navigateToQueueIndex(queueIndex + 1)}
                disabled={queueLoading || queueIndex < 0 || queueIndex >= queueHashes.length - 1}
                className="rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Stimulus Details
          </h2>
          <div className="mt-4 flex gap-8">
            <div className="relative">
              {imageUrl && (
                <>
                  <img
                    ref={imageRef}
                    src={imageUrl}
                    alt={`Stimulus ${hash}`}
                    width={240}
                    height={360}
                    className="rounded-lg shadow-lg object-contain bg-gray-50"
                    style={{ width: '240px', height: '360px' }}
                    onLoad={() => setImageLoaded(true)}
                  />
                  <canvas
                    ref={canvasRef}
                    width={240}
                    height={360}
                    className="absolute top-0 left-0 cursor-crosshair"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={() => setIsDrawing(false)}
                  />
                </>
              )}
              <div className="mt-4 text-sm text-gray-500">
                <p className="font-mono">Hash: {hash}</p>
              </div>
            </div>

            <div className="w-64">
              <h3 className="text-sm font-medium text-gray-900 mb-4">Labels</h3>
              {stimulus?.labeling_complete === true ? (
                <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-xs font-semibold text-emerald-800">labeling complete (manual)</p>
                  <button
                    type="button"
                    onClick={() => updateLabelingCompletion(false)}
                    disabled={stateUpdating}
                    className="mt-2 text-sm font-semibold text-emerald-800 hover:text-emerald-950 disabled:opacity-50"
                  >
                    Return to label queue
                  </button>
                </div>
              ) : isLabelingIncomplete({ ...stimulus, labels }) ? (
                <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs text-amber-800">Missing one or more required AOI labels.</p>
                  <button
                    type="button"
                    onClick={() => updateLabelingCompletion(true)}
                    disabled={stateUpdating}
                    className="mt-2 text-sm font-semibold text-amber-900 hover:text-amber-950 disabled:opacity-50"
                  >
                    Mark labeling complete
                  </button>
                </div>
              ) : null}
              {stateError && (
                <p role="alert" className="mb-4 text-sm font-medium text-red-700">{stateError}</p>
              )}

              {/* Pending Label Form */}
              {pendingLabel && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Label Type
                  </label>
                  <select
                    value={labelName}
                    onChange={(e) => setLabelName(e.target.value)}
                    className="block w-full rounded-md border-0 py-1.5 px-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 mb-2"
                  >
                    <option value="">Select a label...</option>
                    {LABEL_OPTIONS.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveLabel}
                      disabled={!labelName}
                      className="flex-1 rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelLabel}
                      className="flex-1 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Existing Labels */}
              <div className="space-y-2">
                {Object.entries(labels).map(([name, coords]) => (
                  <div
                    key={name}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-md text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDeleteLabel(name)}
                        className="text-gray-400 hover:text-red-600 p-1 rounded hover:bg-red-50"
                        title="Delete label"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                      <span className="font-medium">{name}</span>
                    </div>
                    <span className="text-gray-500 font-mono text-xs">
                      {`{x1:${coords.x1}, x2:${coords.x2}, y1:${coords.y1}, y2:${coords.y2}}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 
